import React from 'react';
import { Button, Form, Input, message, Radio, Space } from 'antd';
import { WorkspaceItemDto } from '@api/Api';
import { useCreateTemplate } from '@hooks/queries/awTemplate';
import type { AwState } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';

import './ManageTemplate.less';

type SaveTemplateProps = {
  onCancel: () => void;
  onFinish: (templateId?: string) => void;
  awState: AwState;
  currentWorkspace: WorkspaceItemDto;
  initialValues?: FormValues;
};

type FormValues = {
  saveAsTemplate: string;
  templateName: string;
};

export default function SaveTemplate({
  onCancel,
  onFinish,
  awState,
  currentWorkspace,
  initialValues = {
    saveAsTemplate: 'no',
    templateName: ''
  }
}: SaveTemplateProps) {
  const { mutateAsync: createTemplate, isPending: isLoadingCreateTemplate } =
    useCreateTemplate({});
  const [form] = Form.useForm<FormValues>();
  const saveAsTemplate = Form.useWatch('saveAsTemplate', form);

  const handlerError = (error?: string) => {
    if (error === 'aw_template_name_already_exists') {
      form.setFields([
        {
          name: 'templateName',
          errors: ['Template with this name already exists']
        }
      ]);
      return;
    }
    message.error({
      content: error || 'Something went wrong'
    });
  };

  const onCreateTemplate = async (values: FormValues) => {
    try {
      if (!currentWorkspace?.id) return;
      if (values.saveAsTemplate === 'no') {
        onFinish();
        return;
      }
      const resTemplate = await createTemplate({
        workspaceId: currentWorkspace.id,
        name: values.templateName,
        lockDecisions: awState.lockDecisions,
        disableNonApprovedDownloads: awState.disableNonApprovedDownloads,
        newVersionBehavior: awState.newVersionBehavior,
        password: awState.password,
        customMessage: awState.customMessage,
        allowAllVersions: awState.allowAllVersions,
        allowAllComments: awState.allowAllComments,
        stages: (awState.stages || []).map((item) => ({
          name: item.name,
          completionCondition: item.completionCondition,
          members: item.members.map((member) => ({
            email: member.email,
            canComment: member.canComment,
            canSubmitDecision: member.canSubmitDecision,
            canUploadNewVersion: member.canUploadNewVersion,
            canManageAW: member.canManageAW,
            canDownload: member.canDownload,
            hasCommentPriority: member.hasCommentPriority,
            hasDominatingDecision: member.hasDominatingDecision
          }))
        }))
      });

      if ('errorCode' in resTemplate.data) {
        handlerError(resTemplate.data.errorCode as string);
        return;
      }
      onFinish(resTemplate?.data?.template?.id);
    } catch (error: any) {
      handlerError(error?.response?.data?.errorCode as string);
    }
  };

  return (
    <div className="modal_container manage-template">
      <h2
        className="modal_container__title"
        style={{ fontSize: 22, marginBottom: '16px' }}
      >
        Save as a template?
      </h2>
      <div
        className="modal_container__text"
        style={{ fontSize: 14, marginBottom: '32px' }}
      >
        Would you like to save this approval routing workflow as a template for
        future use?
      </div>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={onCreateTemplate}
      >
        <Form.Item name="saveAsTemplate">
          <Radio.Group size="small">
            <Space direction="vertical" size="middle">
              <Radio value="no">No</Radio>
              <Radio value="yes">Yes, save template</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        {saveAsTemplate === 'yes' && (
          <Form.Item
            label="Name approval routing template"
            name="templateName"
            rules={[{ required: true, message: 'Name required.' }]}
          >
            <Input size="large" />
          </Form.Item>
        )}
        <Space
          size="large"
          style={{ justifyContent: 'flex-end', width: '100%' }}
        >
          <Button onClick={onCancel} type="text">
            Cancel
          </Button>
          <Button
            loading={isLoadingCreateTemplate}
            type="primary"
            htmlType="submit"
            style={{ minWidth: 152 }}
          >
            Done
          </Button>
        </Space>
      </Form>
    </div>
  );
}
